import React, { useMemo, useState } from 'react';
import { Copy, ExternalLink, QrCode, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { getOuroMobileShellUrl, OURO_APP_NAME } from '../../lib/ouroMobileUrl';

const agencySans = "font-[Calibri,'Calibri_Light','Segoe_UI',sans-serif]";

/**
 * QR + copy block for handing desktop visitors into the Ouro mobile shell.
 */
export function JessicaMobileHandoffCard({ compact = false, className = '' }) {
  const mobileUrl = useMemo(() => getOuroMobileShellUrl(), []);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(mobileUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      className={`rounded-2xl border border-[#ead6ff] bg-[#faf5ff] p-5 sm:p-6 ${agencySans} ${className}`}
      aria-labelledby="ouro-mobile-handoff-title"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ead6ff] bg-white text-[#7c3aed]">
          <Smartphone className="h-5 w-5" strokeWidth={1.5} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p id="ouro-mobile-handoff-title" className="text-base font-semibold text-neutral-900">
            Try {OURO_APP_NAME} on your phone
          </p>
<p className="mt-1 text-sm leading-relaxed text-neutral-600">
              Open the mobile experience to test a memory-first AI assistant built for quick conversations,
              saved summaries, and reusable context. Add it to your home screen for lightweight access
              without an app store install.
            </p>
        </div>
      </div>

      <div className={`mt-5 grid gap-5 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)]'}`}>
        <div className="mx-auto flex flex-col items-center gap-2 sm:mx-0">
          <div className="rounded-xl border border-[#ead6ff] bg-white p-3 shadow-sm">
            <QRCodeSVG
              value={mobileUrl}
              size={compact ? 148 : 176}
              level="M"
              includeMargin
              aria-label={`QR code for ${mobileUrl}`}
            />
          </div>
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.12em] text-[#7c3aed]">
            <QrCode className="h-3.5 w-3.5" aria-hidden />
            Scan to open
          </p>
        </div>

        <div className="flex min-w-0 flex-col justify-center gap-3">
          <ol className="space-y-2 text-sm leading-relaxed text-neutral-600">
            <li>
              <span className="font-semibold text-neutral-800">1.</span> Scan with your phone camera.
            </li>
            <li>
              <span className="font-semibold text-neutral-800">2.</span> Open the {OURO_APP_NAME} mobile shell in your browser.
            </li>
            <li>
              <span className="font-semibold text-neutral-800">3.</span> Use Share / menu → <strong>Add to Home Screen</strong>.
            </li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <a
              href={mobileUrl}
              className="inline-flex items-center gap-2 rounded-full border border-[#c4b5fd] bg-white px-4 py-2 text-sm font-semibold text-[#6d28d9] transition-colors hover:bg-[#f5f3ff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600]"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              Open mobile link
            </a>
            <button
              type="button"
              onClick={() => void copyLink()}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600]"
            >
              <Copy className="h-4 w-4" aria-hidden />
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>

          <p className="break-all text-xs text-neutral-500">{mobileUrl}</p>
        </div>
      </div>
    </section>
  );
}

export default JessicaMobileHandoffCard;
